/**
 * Cleanup Progress Provider
 *
 * Manages cleanup steps state with API integration and polling for OCU agent.
 * Automatically fetches steps when thread is created and polls for updates.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
  useState,
} from 'react';
import { useAppSelector } from '@/stores/store';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { STORAGE_KEYS } from '@/config/storage-config';
import { appConfig } from '@/config/app-config';
import type { CurrentTeam } from '@/types/onboarding.types';

/**
 * Number of retry attempts on failure
 */
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Polling interval in milliseconds
 */
const POLLING_INTERVAL = 5000;

export interface CleanupStep {
  step_name: string;
  display_name: string;
  status: 'Created' | 'In progress' | 'Complete';
  issues_found: number;
  issues_resolved: number;
}

interface CleanupProgressContextValue {
  steps: CleanupStep[];
  isLoading: boolean;
  currentStep: CleanupStep | null;
  issuesFound: number;
  issuesResolved: number;
  scopeSelected: boolean;
  currentTeam: CurrentTeam | null;
  refetch: () => Promise<void>;
}

const defaultContextValue: CleanupProgressContextValue = {
  steps: [],
  isLoading: false,
  currentStep: null,
  issuesFound: 0,
  issuesResolved: 0,
  scopeSelected: false,
  currentTeam: null,
  refetch: async () => {},
};

const CleanupProgressContext = createContext<CleanupProgressContextValue>(defaultContextValue);

interface CleanupProgressProviderProps {
  children: ReactNode;
  threadId: string | null;
}


export function CleanupProgressProvider({
  children,
  threadId,
}: CleanupProgressProviderProps) {
  const [steps, setSteps] = useState<CleanupStep[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [scopeSelected, setScopeSelected] = useState(false);
  const [currentTeam, setCurrentTeam] = useState<CurrentTeam | null>(null);
  const selectedAgent = useAppSelector(selectSelectedAgent);

  const retryCountRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentTeamFoundRef = useRef<boolean>(false);

  /**
   * Fetch cleanup steps from API
   */
  const fetchSteps = useCallback(async () => {
    if (!threadId) {
      setSteps([]);
      return;
    }

    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      console.warn('No auth token available');
      return;
    }

    setIsLoading(true);

    try {
      const deploymentUrl = appConfig.API_BASE_URL;
      const agentId = selectedAgent?.id || 'optimize-clean-up-agent';
      const response = await fetch(`${deploymentUrl}/agents/${agentId}/threads/${threadId}/agent-steps`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cleanup steps: ${response.status}`);
      }

      const data = await response.json();

      if (isMountedRef.current) {
        const fetched: CleanupStep[] = (data.steps || []).map((s: CleanupStep) => ({
          ...s,
          issues_found: s.issues_found ?? 0,
          issues_resolved: s.issues_resolved ?? 0,
        }));
        setSteps(fetched);
        // scopeSelected = any step beyond quick_discovery has been inserted
        setScopeSelected(fetched.some((s) => s.step_name !== 'quick_discovery'));
        retryCountRef.current = 0;
      }

      // Fetch current team if not yet found
      if (isMountedRef.current && !currentTeamFoundRef.current) {
        const agentId = selectedAgent?.id || 'optimize-clean-up-agent';
        try {
          const teamRes = await fetch(
            `${appConfig.API_BASE_URL}/agents/${agentId}/threads/${threadId}/current-team`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          if (teamRes.ok) {
            const teamData = await teamRes.json();
            if (teamData?.id && isMountedRef.current) {
              setCurrentTeam(teamData);
              currentTeamFoundRef.current = true;
            }
          }
        } catch (teamError) {
          // Non-fatal — team will be fetched on next poll
          console.warn('Failed to fetch current team:', teamError);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch cleanup steps:', error);

      // Silent retry logic
      if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
        retryCountRef.current += 1;
        setTimeout(() => {
          if (isMountedRef.current) {
            fetchSteps();
          }
        }, 1000 * retryCountRef.current);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [threadId, selectedAgent]);

  // Clear all state immediately when thread changes (before async fetch completes)
  useEffect(() => {
    setSteps([]);
    setScopeSelected(false);
    currentTeamFoundRef.current = false;
    setCurrentTeam(null);
  }, [threadId]);

  // Calculate derived values (must be before useEffects that reference them)
  const currentStep = steps.find((step) => step.status === 'In progress') || null;

  // Fetch steps on mount and when threadId changes
  useEffect(() => {
    isMountedRef.current = true;
    fetchSteps();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchSteps]);

  // Set up polling for updates — stops automatically once the 'end' step is Complete
  useEffect(() => {
    if (!threadId) return;

    pollingIntervalRef.current = setInterval(() => {
      if (!isMountedRef.current) return;
      // Stop polling once the 'end' step is Complete
      const endStep = steps.find((s) => s.step_name === 'end');
      if (endStep?.status === 'Complete') {
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        return;
      }
      fetchSteps();
    }, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, fetchSteps]);

  // Calculate remaining derived values
  const issuesFound = steps.reduce((sum, step) => sum + (step.issues_found || 0), 0);
  const issuesResolved = steps.reduce((sum, step) => sum + (step.issues_resolved || 0), 0);

  const contextValue: CleanupProgressContextValue = {
    steps,
    isLoading,
    currentStep,
    issuesFound,
    issuesResolved,
    scopeSelected,
    currentTeam,
    refetch: fetchSteps,
  };

  return (
    <CleanupProgressContext.Provider value={contextValue}>
      {children}
    </CleanupProgressContext.Provider>
  );
}

export function useCleanupProgress(): CleanupProgressContextValue {
  const context = useContext(CleanupProgressContext);
  if (!context) {
    throw new Error('useCleanupProgress must be used within a CleanupProgressProvider');
  }
  return context;
}
